import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import {
  PromptInput,
  PromptInputBody,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputTools,
  PromptInputSubmit,
} from '@/components/ai-elements/prompt-input';
import { VoiceOrb } from '@/components/ai-elements/voice-orb';
import { VoiceStatusLine } from '@/components/ai-elements/voice-status-line';
import { Button } from '@/components/ui/button';
import { PhoneOff } from 'lucide-react';
import { useAzureRealtimeAdapter } from '@/hooks/useAzureRealtimeAdapter';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { useInterviewStore } from './useInterviewStore';
import type { ExtractedEntity, ExtractedRelationship } from './useInterviewStore';

// ── Props ─────────────────────────────────────────────────────────────────

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  isStreaming?: boolean;
  /**
   * Realtime mode props — when provided the VoiceOrb is shown
   * (subject to the backend feature-flag).
   */
  sessionId?: string | null;
  workspaceId?: string | null;
  topic?: string;
  templateId?: string;
  /** Called to obtain a fresh Bearer token before connecting. */
  getToken?: () => Promise<string | null>;
}

// ── Component ─────────────────────────────────────────────────────────────

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'Deine Antwort eingeben…',
  isStreaming = false,
  sessionId,
  workspaceId,
  topic,
  templateId,
  getToken,
}: ChatInputProps) {
  // ── Text mode state ────────────────────────────────────────────────────
  const [value, setValue] = useState('');

  // ── Voice mode state ───────────────────────────────────────────────────
  const [mode, setMode] = useState<'text' | 'voice'>('text');
  const [realtimeEnabled, setRealtimeEnabled] = useState(false);
  const [realtimeChecked, setRealtimeChecked] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [isMuted] = useState(false);

  // Refs for stable callbacks
  const hasStartedResponseRef = useRef(false);
  const hasAddedUserMessageRef = useRef(false);
  const currentTranscriptRef = useRef('');
  const lastActivityRef = useRef<number>(Date.now());
  const [inactivityCountdown, setInactivityCountdown] = useState(60);

  // ── Voice store ────────────────────────────────────────────────────────
  const audioOutputEnabled = useVoiceStore((s) => s.audioOutputEnabled);
  const voiceProvider = useVoiceStore((s) => s.provider);
  const availableProviders = useVoiceStore((s) => s.availableProviders);
  const azureAvailable = availableProviders.find((entry) => entry.id === 'azure-realtime')?.available ?? false;
  const hasProviderMetadata = availableProviders.length > 0;

  // ── Store realtime actions ─────────────────────────────────────────────
  const {
    addRealtimeUserMessage,
    updateRealtimeUserMessageText,
    startRealtimeResponse,
    appendRealtimeToken,
    completeRealtimeResponse,
    updateLastTurnEntities,
  } = useInterviewStore();

  // ── Feature-flag: check backend once when session is available ─────────
  useEffect(() => {
    if (!sessionId || !workspaceId) {
      setRealtimeEnabled(false);
      setRealtimeChecked(false);
      return;
    }

    let cancelled = false;
    setRealtimeChecked(false);

    void (async () => {
      try {
        const token = getToken ? await getToken() : null;
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch('/api/python/interview/realtime/enabled', { headers });
        if (!response.ok) {
          if (!cancelled) {
            setRealtimeEnabled(false);
          }
          return;
        }

        const data = (await response.json()) as { enabled?: boolean };
        if (!cancelled) {
          setRealtimeEnabled(Boolean(data?.enabled));
          setRealtimeChecked(true);
        }
      } catch {
        if (!cancelled) {
          setRealtimeEnabled(false);
          setRealtimeChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, workspaceId, getToken]);

  // ── Azure Realtime adapter (VoiceProvider interface) ───────────────────

  const handleSessionEndRequested = useCallback(() => {
    toast.info('Interview wurde auf Wunsch beendet.', { duration: 5000 });
    handleExitRef.current();
  }, []);

  const adapter = useAzureRealtimeAdapter({
    onEntitiesExtracted: useCallback(
      (entities: Record<string, unknown>[], relationships: Record<string, unknown>[]) => {
        updateLastTurnEntities(
          entities as unknown as ExtractedEntity[],
          relationships as unknown as ExtractedRelationship[],
        );
      },
      [updateLastTurnEntities],
    ),
    onSessionEndRequested: handleSessionEndRequested,
  });

  // ── Wire adapter events to interview store ─────────────────────────────

  useEffect(() => {
    const unsubs = [
      adapter.onTranscript((text: string) => {
        setLiveTranscript(text);
        currentTranscriptRef.current = text;
        if (!text) {
          hasAddedUserMessageRef.current = false;
          return;
        }
        if (!hasAddedUserMessageRef.current) {
          addRealtimeUserMessage(text);
          hasAddedUserMessageRef.current = true;
        } else {
          updateRealtimeUserMessageText(text);
        }
      }),
      adapter.onResponse((delta: string) => {
        if (!hasStartedResponseRef.current) {
          if (!hasAddedUserMessageRef.current) {
            addRealtimeUserMessage(currentTranscriptRef.current);
            hasAddedUserMessageRef.current = true;
          }
          startRealtimeResponse();
          hasStartedResponseRef.current = true;
        }
        appendRealtimeToken(delta);
      }),
      adapter.onTurnComplete?.((userTranscript: string, _aiResponse: string) => {
        void userTranscript;
        completeRealtimeResponse();
        hasStartedResponseRef.current = false;
        hasAddedUserMessageRef.current = false;
        currentTranscriptRef.current = '';
      }),
    ];
    return () => {
      unsubs.forEach((unsub) => unsub?.());
    };
  }, [
    adapter,
    addRealtimeUserMessage,
    updateRealtimeUserMessageText,
    startRealtimeResponse,
    appendRealtimeToken,
    completeRealtimeResponse,
  ]);

  // ── Voice mode controls ────────────────────────────────────────────────

  const handleVoiceStart = useCallback(async () => {
    if (!getToken || !sessionId || !workspaceId) return;
    setMode('voice');
    try {
      const tok = await getToken();
      await adapter.connect({
        workspaceId,
        featureContext: 'interview',
        sessionId,
        topic,
        templateId,
        token: tok,
        audioOutput: audioOutputEnabled,
        voiceId: useVoiceStore.getState().selectedVoice,
      });
    } catch (err) {
      logger.error('ChatInput', 'VoiceOrb connect failed', err);
      toast.error('Sprachsitzung konnte nicht gestartet werden.', { duration: 6000 });
      setMode('text');
    }
  }, [getToken, sessionId, workspaceId, topic, templateId, adapter, audioOutputEnabled]);

  const handleVoiceStop = useCallback(() => {
    void adapter.disconnect();
    try { completeRealtimeResponse(); } catch { /* ignore */ }
    setMode('text');
    setLiveTranscript('');
    hasStartedResponseRef.current = false;
    hasAddedUserMessageRef.current = false;
    currentTranscriptRef.current = '';
  }, [adapter, completeRealtimeResponse]);

  // Stable ref for inactivity / session-end callbacks
  const handleExitRef = useRef(handleVoiceStop);
  handleExitRef.current = handleVoiceStop;

  // ── Inactivity auto-disconnect ─────────────────────────────────────────

  useEffect(() => {
    if (mode !== 'voice') return;
    lastActivityRef.current = Date.now();
  }, [mode, adapter.status]);

  useEffect(() => {
    const INACTIVITY_S = 60;
    if (mode !== 'voice') {
      setInactivityCountdown(INACTIVITY_S);
      return;
    }
    lastActivityRef.current = Date.now();
    setInactivityCountdown(INACTIVITY_S);
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, INACTIVITY_S - elapsed);
      setInactivityCountdown(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        toast.info('Sprachsitzung beendet – keine Aktivität für 60 Sekunden.', { duration: 5000 });
        handleExitRef.current();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [mode]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (mode === 'voice') {
        void adapter.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Text mode handlers ─────────────────────────────────────────────────

  const handleSubmit = useCallback(
    (message: { text: string }) => {
      const trimmed = message.text.trim();
      if (!trimmed || disabled) return;
      onSend(trimmed);
      setValue('');
    },
    [onSend, disabled],
  );

  const status = isStreaming ? ('streaming' as const) : ('ready' as const);
  const isVoice = mode === 'voice';
  const interviewVoiceSupported = Boolean(
    realtimeEnabled
    && sessionId
    && workspaceId
    && voiceProvider === 'azure-realtime'
    && (!hasProviderMetadata || azureAvailable),
  );
  const interviewVoiceUnavailableReason = !realtimeChecked
    ? null
    : !realtimeEnabled
      ? 'Sprachinterview ist in dieser Umgebung deaktiviert.'
      : voiceProvider !== 'azure-realtime'
        ? 'Das aktive Voice-Profil nutzt nicht Azure Realtime. Der Interviewer unterstuetzt derzeit nur Azure Realtime.'
        : hasProviderMetadata && !azureAvailable
          ? 'Azure Realtime ist fuer den Interviewer aktuell nicht konfiguriert.'
          : null;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="border-t border-border bg-background px-4 py-3">
      <PromptInput
        onSubmit={isVoice ? () => {} : handleSubmit}
        className={cn(
          'w-full transition-colors',
          isVoice && adapter.status === 'listening' && 'ring-1 ring-voice-listening/40',
          isVoice && adapter.status === 'thinking' && 'ring-1 ring-voice-thinking/40',
          isVoice && adapter.status === 'speaking' && 'ring-1 ring-voice-speaking/40',
        )}
      >
        <PromptInputBody className="relative">
          {isVoice ? (
            <VoiceStatusLine
              status={adapter.status}
              transcript={liveTranscript}
            />
          ) : (
            <PromptInputTextarea
              value={value}
              onChange={(e) => setValue(e.currentTarget.value)}
              placeholder={placeholder}
              disabled={disabled}
            />
          )}
        </PromptInputBody>

        <PromptInputFooter>
          <PromptInputTools>
            {/* VoiceOrb — mic button (no panel, controls are inline above) */}
            {interviewVoiceSupported && (
              <VoiceOrb
                variant="inline"
                showPanel={false}
                status={isVoice ? adapter.status : 'idle'}
                isMuted={isMuted}
                onStart={handleVoiceStart}
                onStop={handleVoiceStop}
              />
            )}
            {!isVoice && interviewVoiceUnavailableReason && (
              <span className="max-w-56 text-[10px] leading-relaxed text-muted-foreground">
                {interviewVoiceUnavailableReason}
              </span>
            )}
          </PromptInputTools>

          {isVoice ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleVoiceStop}
              aria-label="Sprachsitzung beenden"
              className="gap-1.5"
            >
              <PhoneOff className="h-3.5 w-3.5" />
              Beenden
            </Button>
          ) : (
            <PromptInputSubmit
              status={status}
              disabled={disabled || (!value.trim() && !isStreaming)}
            />
          )}
        </PromptInputFooter>
      </PromptInput>

      <p className="mt-1.5 text-center text-[10px] text-muted-foreground/60">
        {isVoice
          ? `Server-VAD aktiv (4 s Stille) · Auflegen in ${inactivityCountdown} s`
          : 'Shift+Enter für neue Zeile · Mikrofon für Spracheingabe · Die KI extrahiert Wissen aus deinen Antworten'}
      </p>
    </div>
  );
}